<template>
  <div :class="`plan-settings ${(activeSubscription && !hasSubscriptionPlan) || plans.length === 0 ? 'centerize' : ''}`">
    <template v-if="activeSubscription && !hasSubscriptionPlan">
      <div class="ta-c page-placeholder-text">
        <h2>
          Your plan is being managed manually.
        </h2>
        <p>
          <a class="link blue" href="mailto:sales@streamify.io">Contact sales</a> for more information.
        </p>
      </div>
    </template>
    <template v-else>
      <template v-if="plans.length > 0">
        <div class="prices p-t-4">
          <div class="prices-header-one" v-if="shoppingSettings.provider !== 'shopline'">
            <div class="prices-header-one-two">
              <Toggle>
                <p :class="`${paymentInterval === 'yearly' ? 'active' : ''}`" @click="paymentInterval = 'yearly'">{{ $t('settings.plan.pay_yearly') }}</p>
                <p :class="`${paymentInterval === 'monthly' ? 'active' : ''}`" @click="paymentInterval = 'monthly'">{{ $t('settings.plan.pay_monthly') }}</p>
              </Toggle>
            </div>
          </div>
          <div class="hide-largescreen" v-if="selectPlan === ''">
            <div class="prices-header-one">
              <div class="prices-header-one-two">
                <label>{{ $t('settings.plan.packages') }}</label>
                <Toggle>
                  <p v-for="(plan, index) in plans" :key="index" :class="`${viewingPlan === index ? 'active' : ''}`" @click="viewingPlan = index">{{ plan.title }}</p>
                </Toggle>
              </div>
            </div>
          </div>
          <small class="m-b-4">{{ $t('settings.plan.trial') }}</small>
          <div :class="`plans m-t-4 ${selectPlan !== '' ? 'lone' : ''}`">
            <Plan v-for="(plan, index) in plans" v-if="selectPlan === '' || (selectPlan !== '' && plan.title.toLowerCase() === selectPlan)" :lone="selectPlan !== ''" :plan="plan" :successPath="successPath" :cancelPath="cancelPath" :key="index" :interval="paymentInterval" :selected="index === selectedPlan" :class="`${viewingPlan === index ? 'viewing' : ''}`" :currency="currency" />
          </div>
          <label class="m-b-4 m-t-2 link" @click="$emit('showAllPlans', true)" v-if="selectPlan !== ''">{{ $t('settings.plan.show_all_plans') }}</label>
        </div>
      </template>
      <template v-else>
        <div class="loading-spinner large"></div>
        <small class="m-t-1">Loading plans</small>
      </template>
    </template>
  </div>
</template>

<script>

import Plan from '@/components/Plans/Plan.vue';

export default {
  components: {
    Plan,
  },
  props: {
    successPath: {
      type: String,
      default: '/settings/plan?status=success',
    },
    cancelPath: {
      type: String,
      default: '/settings/plan?status=canceled',
    },
    selectPlan: {
      type: String,
      default: '',
    },
  },
  computed: {
    /**
     * Returns a list of plans
     * @returns {Array}
     */
    plans() {
      const plans = this.$store.getters['Plan/plans'];
      return this.shoppingSettings.provider === 'shopline' ? plans.slice(1, 3).map((plan, index) => ({
        ...plan,
        title: index === 0 ? 'Shopline' : plan.title,
        checkoutParams: {
          ...plan.checkoutParams,
          plan: 'shopline',
        },
        currencies: index === 0 ? [{
          priceMonth: 500,
          priceYear: 12 * 500,
          from: false,
          currency: {
            name: 'eur',
            symbol: '€',
            symbolAfter: false,
          },
        }] : [],
      })) : plans;
    },
    /**
     * Returns a checkoutlink after it has been generated
     * @returns {String}
     */
    checkoutLink() {
      return this.$store.getters['Plan/checkoutLink'];
    },
  },
  watch: {
    accountSettings: {
      immediate: true,
      handler(settings) {
        if (!settings || !settings.stripe_plan) {
          return;
        }

        if (settings.stripe_plan.subscription_status === 'active' && settings.stripe_plan.subscription_id !== '') {
          this.$router.push('/settings/billing');
        }
      },
    },
    checkoutLink: {
      immediate: true,
      handler(url) {
        if (url) {
          window.location.href = url;
        }
      },
    },
  },
  data() {
    return {
      selectedPlan: -1,
      viewingPlan: 0,
      success: null,
      error: null,
      paymentInterval: 'yearly',
      currency: 'eur',
    };
  },
  async mounted() {
    if (this.$route.query && this.$route.query.status) {
      switch (this.$route.query.status) {
        case 'success':
          // Forward to billing
          this.$router.push('/settings/billing/success');
          return;
        case 'cancelled':
          this.error = true;
          break;
        default:
      }
    }

    await this.$store.dispatch('Plan/getPlans');
    await this.$store.dispatch('AccountManager/getAccountSettings');
  },
};
</script>

<style lang="scss">
.plans.lone .plan header {
  height: auto;
}
</style>

<style lang="scss" scoped>
.plan-settings {
  &.centerize {
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
  }
}

.hide-largescreen {
  @media (min-width: 1024px) {
    display: none;
  }
}
.plans {
  display: flex;
  width: 100%;
  justify-content: center;

  &.lone {
    justify-content: center;

    & > * {
      flex: none;
    }
  }

  & > * {
    @media (max-width: 1023px) {
      width: 100%;
      &:not(.viewing) {
        display: none;
      }
    }
  }

  @media (min-width: 1024px) {
    flex-wrap: wrap;

    & > * {
      margin: 0 .25rem 1rem;
      width: calc(33.333% - .5rem);
    }
  }

  @media (min-width: 1400px) {
    flex-wrap: nowrap;

    & > * {
      flex: 1;
      width: auto;
    }
  }
}

.prices {
  align-items: center;
  display: flex;
  flex-direction: column;
}

.prices-header-one {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;

  .prices-header-one-two {
    text-align: center;
    margin: 0 0.25rem;

    @media (min-width: 1024px) {
      align-items: center;
      display: flex;
    }

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;

      .toggle {
        margin-right: 0;
      }
    }
  }

  label {
    font-size: .75rem;
    color: #000000;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
  }
}
</style>
